/**
 * Ported VERBATIM from `apr_template/src/services/aiRepositoryApi.ts`.
 * Only import paths change. See ../compat/ for the shims.
 */
import api from './aprAxios';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface RepositoryRow {
  app_id: string;
  application_id_code: string | null;
  application_name: string;
  vendor: string | null;
  excluded_from_apr: boolean;
  current_run_app_id: string;
  origin?: string;
  disposition: string | null;
  confidence: string | null;
  savings_low: number | null;
  savings_mid: number | null;
  savings_high: number | null;
  current_annual_cost: number | null;
  future_run_rate: number | null;
  run_id: string;
  run_name: string;
  analyzed_at: string | null;
  pinned: boolean;
  analyses_count: number;
  /** Signals that this app's analyses disagree — see disagreement() in AnalysisRepository. */
  disposition_differs?: boolean;
  one_time_cost_min?: number | null;
  one_time_cost_max?: number | null;
}

export interface AnalysisHistoryItem {
  run_app_id: string;
  run_id: string;
  run_name: string;
  run_status: string;
  completed_at: string | null;
  created_at: string | null;
  disposition: string | null;
  confidence: string | null;
  rationale: string | null;
  disposition_justification: string | null;
  savings_low: number | null;
  savings_mid: number | null;
  savings_high: number | null;
  savings_pct_low: number | null;
  savings_pct_mid: number | null;
  savings_pct_high: number | null;
  current_annual_cost: number | null;
  future_run_rate: number | null;
  disposition_cost: number | null;
  disposition_cost_low: number | null;
  disposition_cost_high: number | null;
  integration_cost: number | null;
  integration_cost_low: number | null;
  integration_cost_high: number | null;
  total_one_time_cost: number | null;
  total_one_time_cost_low: number | null;
  total_one_time_cost_high: number | null;
  disposition_timeline: string | null;
  strategic_options: string | null;
  methodology_note: string | null;
  mapped_framework_label: string | null;
  origin?: string;
  derived_from_ids?: string[] | null;
  pinned: boolean;
  /** This analysis is the app's elected disposition of record (pinned as final). */
  pinned_final?: boolean;
}

export interface AppAnalyses {
  application: { id: string; application_id_code: string | null; name: string; vendor: string | null };
  /** The framework's own Q&A-driven disposition — separate from the AI/client analyses. */
  qa?: {
    answered: number;
    total: number;
    completed: boolean;
    disposition: string | null;
    overall_score: number | string | null;
  };
  pinnedRunAppId: string | null;
  currentRunAppId: string | null;
  /** The analysis pinned as "final" — the disposition of record (null = none). */
  finalRunAppId: string | null;
  analyses: AnalysisHistoryItem[];
}

export interface PinFinalResult {
  message: string;
  finalRunAppId: string;
  disposition: string | null;
  mappedFrameworkLabel: string | null;
  /** The analysis narrative to adopt as the Final Disposition justification. */
  narrative: string;
}

// ─── API ─────────────────────────────────────────────────────────────────────

export async function getAnalysisRepository(): Promise<RepositoryRow[]> {
  const res = await api.get('/ai/analysis-repository');
  return res.data;
}

export async function getAppAnalyses(appId: string): Promise<AppAnalyses> {
  const res = await api.get(`/ai/applications/${appId}/analyses`);
  return res.data;
}

export async function pinAnalysis(appId: string, runAppId: string): Promise<void> {
  await api.post(`/ai/applications/${appId}/analyses/${runAppId}/pin`);
}

export async function unpinAnalysis(appId: string): Promise<void> {
  await api.post(`/ai/applications/${appId}/analyses/unpin`);
}

// Elect an analysis as the app's disposition of record (the one Final Disposition adopts).
export async function pinAnalysisAsFinal(appId: string, runAppId: string): Promise<PinFinalResult> {
  const res = await api.post(`/ai/applications/${appId}/analyses/${runAppId}/pin-final`);
  return res.data;
}

export async function unpinAnalysisAsFinal(appId: string): Promise<void> {
  await api.post(`/ai/applications/${appId}/analyses/unpin-final`);
}

// Record the app's pinned-final analysis AS its final disposition, in one step.
export async function recordFinalFromPinned(appId: string): Promise<void> {
  await api.post(`/final-disposition/adopt/${appId}`);
}

/** The AI's read on which of an app's analyses is better grounded. Advice only. */
export interface CompareVerdict {
  headline: string;
  differences: { field: string; summary: string; why_it_matters: string }[];
  more_realistic_run_app_id: string | null;
  more_realistic_reason: string;
  confidence: 'low' | 'medium' | 'high';
  evidence_needed: string[];
  consolidation_hint: string;
}

// On demand — one Sonnet call (~30s), so it's never run automatically. The verdict is
// saved server-side; getSavedComparison() below returns it without a new call.
export async function askAiWhichIsRealistic(appId: string): Promise<CompareVerdict> {
  const res = await api.post(`/ai/applications/${appId}/analyses/compare`, {}, { timeout: 300000 });
  return res.data;
}

export interface SavedComparison {
  verdict: CompareVerdict | null;
  createdAt?: string;
  // The saved verdict was formed from a different set of analyses than those now on
  // record (a new analysis has completed since), so it no longer describes them all.
  stale?: boolean;
}

// The comparison already produced for this app. Free and instant — no AI call.
export async function getSavedComparison(appId: string): Promise<SavedComparison> {
  const res = await api.get(`/ai/applications/${appId}/analyses/compare`);
  return res.data;
}

// Portfolio Word/PPT deliverables are now generated + cached by the Report
// Library — see services/reportsApi.ts (downloadReport / getPortfolioCurrent).
